// ریز اقلام سند هزینه — پنجرهٔ تمام‌صفحه روی سند هزینه
// props: { user, documentId, programKind (0|1), sendToEvaluation, amount, onClose, onChanged }
import React, { useState, useEffect, useRef } from 'react';
import { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { openReport } from '../../components/report';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';
import HavalehDetailNotes from './HavalehDetailNotes';
import HazinehDocumentDetailDeduction from './HazinehDocumentDetailDeduction';
import ReadPayrolHazineh from './ReadPayrolHazineh';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString()).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
const num = v => { const n = parseInt(toEng(v).replace(/[^0-9-]/g, ''), 10); return Number.isFinite(n) ? n : 0; };
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh-detail${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const FORM = 'FRMHAZINEHDOCUMENTDETAIL';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNDELETEALL', 'BTNOK', 'BTNCANCEL', 'BTNHAZINEH', 'BTNINFORMTORECEIVER', 'BTNHAVALEHDETAILNOTES'];
const SENT_MSG = 'این سند به رسیدگی ارسال شده و امکان تغییر در آن وجود ندارد';
const HZ = ['hazineh_id', 'hazineh_id1', 'hazineh_id2', 'hazineh_id3', 'hazineh_id4', 'hazineh_id5'];
const PAYMENT = ['نقدی', 'غیرنقدی'];
const SHOMOOL = ['', 'داخل شمول', 'خارج شمول'];
const TAX_SITE = 'http://www.Tax.Gov.ir/';

const EMPTY = {
  row: '', factor_no: '', factor_date: '', amount_receiver_id: 0, amount_receiver: '', remark: '', quantity: '', unit_price: '',
  sum_pay: '', discount: '', added_tax: '', is_personel: 3, payment_method: 1, buyer_id: 0, buyer: '',
  tax_refrence_no: '', tax_refrence_date: '', cost_center_id: 0, cost_center: '', credit_id: 0, credit: '',
  program_id: 0, program: '', project_id: 0, project: '',
  hazineh_id: 0, hazineh_id1: 0, hazineh_id2: 0, hazineh_id3: 0, hazineh_id4: 0, hazineh_id5: 0, hz: ['', '', '', '', '', ''],
  shomool: 0, setad_no: '', bill_id: '', slip_id: '', cheque_no: '', cheque_date: '',
};

const fromRow = (r) => ({
  row: r.Row ?? '', factor_no: r.FactorNo || '', factor_date: r.FactorDate || '',
  amount_receiver_id: r.AmountReceiverId || 0, amount_receiver: r.Receiver || '', remark: r.Remark || '',
  quantity: r.Quantity ?? '', unit_price: r.UnitPrice ?? '', sum_pay: r.SumPay ?? '', discount: r.Discount ?? '',
  added_tax: r.AddedTax ?? '', is_personel: Number(r.IsPersonel) || 0, payment_method: Math.max(Number(r.PaymentMethod) || 0, 0),
  buyer_id: r.BuyerId || 0, buyer: r.Buyer || '', tax_refrence_no: r.TaxRefrenceNo || '', tax_refrence_date: r.TaxRefrenceDate || '',
  cost_center_id: r.CostCenterId || 0, cost_center: r.CostCenter || '', credit_id: r.CreditId || 0, credit: r.Credit || '',
  program_id: r.ProgramId || 0, program: r.Program || '', project_id: r.ProjectId || 0, project: r.Project || '',
  hazineh_id: r.HazinehId || 0, hazineh_id1: r.HazinehId1 || 0, hazineh_id2: r.HazinehId2 || 0,
  hazineh_id3: r.HazinehId3 || 0, hazineh_id4: r.HazinehId4 || 0, hazineh_id5: r.HazinehId5 || 0,
  hz: [r.Hz0, r.Hz1, r.Hz2, r.Hz3, r.Hz4, r.Hz5].map(x => x || ''),
  shomool: Number(r.Shomool) || 0, setad_no: r.SetadNo || '', bill_id: r.BillId || '', slip_id: r.SlipId || '',
  cheque_no: r.ChequeNo || '', cheque_date: r.ChequeDate || '',
});

const gridCols = (lv, payKind) => [
  { key: 'Row', label: 'ردیف' }, { key: 'Remark', label: 'شرح', wide: true },
  ...(Number(payKind) === 1 ? [] : [{ key: 'PaymentMethodTitle', label: 'روش پرداخت' }]),
  { key: 'IdNo', label: 'کد/شناسه ملی' }, { key: 'Receiver', label: 'فروشنده', wide: true }, { key: 'AccountNo', label: 'حساب' },
  { key: 'ShebaError', label: 'خطای حساب شبا' }, { key: 'BankName', label: 'بانک' }, { key: 'FactorNo', label: 'فاکتور' },
  { key: 'FactorDate', label: 'تاریخ' }, { key: 'Quantity', label: 'تعداد', num: true }, { key: 'UnitPrice', label: 'مبلغ واحد', money: true },
  { key: 'SumPay', label: 'مبلغ', money: true }, { key: 'AddedTax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'Discount', label: 'تخفیف', money: true }, { key: 'Total', label: 'جمع', money: true },
  { key: 'Deduction', label: 'کسورات', money: true }, { key: 'Net', label: 'خالص', money: true },
  { key: 'RejectCount', label: 'تعداد واخواهی', num: true }, { key: 'Buyer', label: 'کارپرداز' },
  { key: 'CostCenter', label: 'مرکز هزینه' }, { key: 'Credit', label: 'منبع اعتبار', wide: true },
  { key: 'Program', label: 'برنامه', wide: true }, { key: 'Project', label: 'پروژه', wide: true },
  { key: 'Hz0', label: lv[1] }, { key: 'Hz1', label: lv[2] }, { key: 'Hz2', label: lv[3] },
  { key: 'Hz3', label: lv[4] }, { key: 'Hz4', label: lv[5] }, { key: 'Hz5', label: lv[6] },
  { key: 'ShomoolTitle', label: 'شمول' }, { key: 'ChequeNo', label: 'شماره چک' }, { key: 'ChequeDate', label: 'تاریخ چک' },
  { key: 'ChequeAmount', label: 'مبلغ چک', money: true }, { key: 'TaxRefrenceNo', label: 'شماره رهگیری مالیاتی' },
  { key: 'TaxRefrenceDate', label: 'تاریخ رهگیری مالیاتی' }, { key: 'SetadNo', label: 'شماره ستاد' },
  { key: 'BillId', label: 'شناسه قبض' }, { key: 'SlipId', label: 'شناسه پرداخت' },
  { key: 'StockSlipNo', label: 'قبض انبار' }, { key: 'StockSlipDate', label: 'تاریخ قبض انبار' },
  { key: 'Havaleh', label: 'حواله' }, { key: 'HavalehNotes', label: 'یادداشت حواله', num: true },
  { key: 'ReqWithSlip', label: 'درخواست های دارای قبض انبار' }, { key: 'ReqWithoutSlip', label: 'درخواستهای فاقد قبض انبار' },
  { key: 'ReqBuildingRepair', label: 'درخواستهای تعمیر ساختمانها' }, { key: 'ReqBuildingFacilities', label: 'درخواستهای منصوبات ساختمانها' },
  { key: 'ReqBuildingInsure', label: 'درخواستهای بیمه ساختمانها' }, { key: 'ReqFlight', label: 'درخواستهای بلیط هواپیما' },
  { key: 'ReqCarRepair', label: 'درخواستهای تعمیرات خودرو' }, { key: 'ReqCarFuel', label: 'درخواستهای سوخت خودرو' },
  { key: 'ReqCarOil', label: 'درخواستهای روغن خودرو' }, { key: 'ReqCarInsure', label: 'درخواستهای بیمه خودرو' },
];
const STAT_COLS = [
  { key: 'Remark', label: 'شرح' }, { key: 'Recv', label: 'دریافتی تنخواه', money: true },
  { key: 'Paid', label: 'پرداختی', money: true }, { key: 'Remain', label: 'موجودی', money: true, sign: true },
  { key: 'Place', label: 'محل اعتبار', wide: true },
];

const SOURCES = [
  ['goods', 'خرید'], ['services', 'خدمات انجام شده'], ['car_fuel', 'سوخت خودرو'], ['car_oil', 'روغن خودرو'],
  ['car_repair', 'تعمیر و نگهداری خودرو'], ['car_insure', 'بیمه خودرو'], ['building_facilities', 'منصوبات ساختمان'],
  ['building_repair', 'تعمیر و نگهداری ساختمان'], ['building_insure', 'بیمه ساختمان'], ['flight', 'بلیط هواپیما'],
];
const DETAIL_SOURCES = ['goods', 'services', 'car_fuel', 'car_oil'];
const MONEY_KEYS = ['مبلغ', 'جمع مبلغ', 'جمع', 'تخفیف', 'مالیات بر ارزش افزوده', 'جمع درخواست'];
// فیلدهای جستجو در فهرست هزینه‌های فاقد سند: [متنی، عددی]
const PENDING_SEARCH = {
  goods: d => [[...(d ? ['ثبت کننده'] : []), 'نام کالا', 'فروشنده', 'فاکتور'], ['جمع مبلغ']],
  services: () => [['واحداجرایی', 'ثبت کننده', 'نام کالا', 'فروشنده', 'فاکتور'], ['جمع مبلغ', 'جمع درخواست', 'مالیات بر ارزش افزوده']],
  car_fuel: () => [['واحداجرایی', 'نام خودرو', 'شماره پلاک'], ['مبلغ']],
  car_oil: () => [['واحداجرایی', 'نام خودرو', 'شماره پلاک'], ['مبلغ']],
  car_repair: () => [['واحداجرایی', 'نام خودرو', 'شماره پلاک', 'نوع تعمیرات', 'تعمیرگاه'], ['مبلغ']],
  car_insure: () => [['واحداجرایی', 'نام خودرو', 'شماره پلاک', 'بیمه گر', 'بیمه نامه'], ['مبلغ']],
  building_facilities: () => [['شهر', 'واحداجرایی', 'کدپستی', 'شرح', 'صورتحساب', 'شماره', 'شناسه پرداخت', 'شناسه قبض'], ['مبلغ']],
  building_repair: () => [['تعمیرکار', 'ساختمان', 'فاکتور'], ['مبلغ']],
  building_insure: () => [['واحداجرایی', 'ساختمان', 'بیمه گر', 'بیمه نامه'], ['مبلغ']],
  flight: () => [['واحداجرایی', 'فروشنده', 'وسیله', 'مسافر'], ['مبلغ']],
};

const isNumericText = t => /^-?\d+(\.\d+)?$/.test(t);
const isLatin = k => /^[a-zA-Z0-9. \-_?]*$/.test(k);
const hasVal = (v) => {
  if (v === null || v === undefined) return false;
  const t = String(v).trim();
  if (t === '' || t === '0') return false;
  return !(isNumericText(t) && Number(t) === 0);
};
const visibleCols = (cols, rows) => cols.filter(c => rows.some(r => hasVal(r[c.key])));
const isNumeric = s => /^-?\d+(\.\d+)?$/.test(String(s).trim());

function Lookup({ value, disabled, fetcher, onPick, onClear }) {
  const [text, setText] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const search = (q) => {
    setText(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetcher(q).then(j => { setItems(j && j.ok ? j.data : []); setOpen(true); });
    }, 300);
  };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: '120px' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input style={{ ...ui.input, ...(disabled ? s.ro : {}) }} value={toFa(text)} disabled={disabled}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onKeyDown={e => { if (!disabled && e.key === 'Delete') onClear(); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {!disabled && value && <button type="button" style={s.xBtn} onClick={onClear}>✕</button>}
      </div>
      {open && !disabled && items.length > 0 && (
        <div style={s.drop}>
          {items.map((it, i) => (
            <div key={i} style={s.dropItem} onMouseDown={() => { onPick(it); setOpen(false); }}>{toFa(it.display)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, width, extra }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: width || 'auto', flex: width ? 'none' : 1, minWidth: width ? undefined : '120px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><label style={ui.flabel}>{label}</label>{extra}</div>
      {children}
    </div>
  );
}

function Pin({ on, onToggle }) {
  return (
    <button type="button" title={on ? 'در ردیف جدید حفظ می‌شود' : 'در ردیف جدید پاک می‌شود'} onClick={onToggle}
      style={{ ...s.pin, opacity: on ? 1 : 0.35 }}>📌</button>
  );
}

function Grid({ cols, rows, selId, idKey, onSelect, onRowHeader, onContext, multi, picked, onPick }) {
  return (
    <table style={ui.table}>
      <thead>
        <tr>
          {onRowHeader && <th style={{ ...ui.th, width: '22px' }} />}
          {multi && <th style={ui.th}>انتخاب</th>}
          {cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={(cols.length || 1) + 2} style={ui.empty}>موردی یافت نشد</td></tr>}
        {rows.map((r, i) => {
          const id = idKey ? r[idKey] : i;
          const sel = multi ? picked.has(id) : (selId !== undefined && selId !== null && id === selId);
          return (
            <tr key={id ?? i} onClick={() => (multi ? onPick(id, i) : onSelect && onSelect(r))}
              onContextMenu={e => { if (onContext) { e.preventDefault(); onContext(r); } }}
              style={{ background: sel ? '#e8f0fe' : (i % 2 ? '#deb887' : '#fff'), cursor: 'pointer' }}>
              {onRowHeader && (
                <td style={{ ...ui.td, color: '#888', fontSize: '10px' }} title="سوابق تغییرات"
                  onClick={e => { e.stopPropagation(); onRowHeader(r); }}>⋯</td>
              )}
              {multi && <td style={ui.td}><input type="checkbox" readOnly checked={sel} /></td>}
              {cols.map(c => (
                <td key={c.key} style={{ ...(c.money ? ui.tdNum : ui.td), ...(c.wide ? s.wide : {}),
                  ...(c.sign && Number(r[c.key]) < 0 ? { color: '#c62828' } : {}), ...(c.sign && Number(r[c.key]) > 0 ? { color: '#1b5e20' } : {}) }}>
                  {c.money ? fmtAmt(r[c.key]) : toFa(r[c.key])}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Popup({ title, cols, rows, onClose }) {
  return (
    <div style={{ ...ui.overlay, zIndex: 1300 }}>
      <div style={{ ...ui.modal, maxWidth: '1100px' }}>
        <div style={ui.mHeader}><span style={ui.mTitle}>{title}</span><button style={ui.closeBtn} onClick={onClose}>✕</button></div>
        <div style={ui.mBody}><Grid cols={cols} rows={rows} /></div>
        <div style={ui.mFooter}><ExcelButton title={title} columns={cols} rows={rows} /></div>
      </div>
    </div>
  );
}

// فهرست هزینه‌های فاقد سند (خرید، خدمات، خودرو، ساختمان، بلیط)
function PendingPanel({ docId, initSource, canAdd, form, onAdded, onClose, say }) {
  const [source, setSource] = useState(initSource || 'goods');
  const [detail, setDetail] = useState(false);
  const [data, setData] = useState({ cols: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const showDetail = DETAIL_SOURCES.includes(source);

  useEffect(() => {
    setLoading(true); setPicked(new Set());
    API(`/documents/${docId}/pending/${source}/?detail=${showDetail && detail ? 1 : 0}`).then(j => {
      setLoading(false);
      if (!j.ok) { say(j.msg); setData({ cols: [], rows: [] }); return; }
      setData({ cols: j.columns || [], rows: j.data || [] });
    });
  }, [docId, source, detail]);

  const srch = toEng(search).trim();
  const rows = data.rows.filter(r => {
    if (!srch || srch === '0') return true;
    const [txt, nums] = PENDING_SEARCH[source](showDetail && detail);
    const like = k => String(r[k] ?? '').includes(srch);
    if (like('گروه کاربری')) return true;
    if (isNumeric(srch) && String(r['شماره درخواست']) === srch) return true;
    if (txt.some(like)) return true;
    return isNumeric(srch) && nums.some(k => Number(r[k]) === Number(srch));
  });
  const cols = visibleCols(data.cols.filter(k => !isLatin(k)).map(k => ({ key: k, label: k, money: MONEY_KEYS.includes(k) })), rows);

  const toggle = (key, i) => {
    const n = new Set(picked);
    if (n.has(key)) n.delete(key); else { n.add(key); setLast(i); }
    setPicked(n);
  };
  const all = () => setPicked(new Set(rows.map(r => r._key)));
  const none = () => setPicked(new Set());
  const reverse = () => setPicked(p => new Set(rows.filter(r => !p.has(r._key)).map(r => r._key)));
  const sameRequest = (on) => {
    if (last === null || !rows[last]) return;
    const no = rows[last]['شماره درخواست'];
    setPicked(p => {
      const n = new Set(p);
      rows.forEach(r => { if (r['شماره درخواست'] === no) { if (on) n.add(r._key); else n.delete(r._key); } });
      return n;
    });
  };
  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    else if (e.key === '+') sameRequest(true);
    else if (e.key === '-') sameRequest(false);
    else if (e.key === '*') reverse();
  };
  const add = async () => {
    setBusy(true);
    const keys = rows.filter(r => picked.has(r._key)).map(r => r._key);
    const j = await API(`/documents/${docId}/pending/${source}/add/`, { method: 'POST',
      body: JSON.stringify({ ...form, keys, detail: showDetail && detail }) });
    setBusy(false);
    if (!j.ok) { say(j.msg); return; }
    onAdded(j.id);
  };
  const count = rows.filter(r => picked.has(r._key)).length;

  return (
    <div style={{ ...ui.overlay, zIndex: 1200 }}>
      <div style={{ ...ui.modal, maxWidth: '1300px', height: '90vh' }} tabIndex={0} onKeyDown={onKey}
        onContextMenu={e => { e.preventDefault(); onClose(); }}>
        <div style={ui.mHeader}><span style={ui.mTitle}>هزینه های فاقد سند</span><button style={ui.closeBtn} onClick={onClose}>✕</button></div>
        <div style={{ padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', borderBottom: '1px solid #eee' }}>
          {SOURCES.map(([k, t]) => (
            <label key={k} style={s.radio}><input type="radio" checked={source === k} onChange={() => setSource(k)} /> {t}</label>
          ))}
          {showDetail && <label style={s.radio}><input type="checkbox" checked={detail} onChange={e => setDetail(e.target.checked)} /> ریزاقلام</label>}
        </div>
        <div style={{ padding: '8px 16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={ui.flabel}>شماره درخواست</label>
          <input style={{ ...ui.searchInput, width: '200px' }} value={search} onChange={e => setSearch(e.target.value)} />
          <button style={s.mini} onClick={all} title="انتخاب همه">+</button>
          <button style={s.mini} onClick={none} title="لغو انتخاب همه">-</button>
          <button style={s.mini} onClick={reverse} title="معکوس انتخاب">*</button>
          <span style={ui.countBadge}>تعداد اقلام {toFa(rows.length)}</span>
          <span style={ui.countBadge}>تعداد انتخاب شده {toFa(count)}</span>
          <ExcelButton title="هزینه های فاقد سند" columns={cols} rows={rows} />
          {count > 0 && canAdd && <button style={ui.btnNew} disabled={busy} onClick={add}>{busy ? '⏳' : 'اضافه کردن به سند هزینه'}</button>}
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? <div style={ui.empty}>⏳</div>
            : <Grid cols={cols} rows={rows} idKey="_key" multi picked={picked} onPick={toggle} />}
        </div>
      </div>
    </div>
  );
}

export default function HazinehDocumentDetail({ user, documentId, programKind = 0, sendToEvaluation = false, amount, onClose, onChanged }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const docId = Number(documentId) || 0;
  const [head, setHead] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selId, setSelId] = useState(null);
  const [mode, setMode] = useState('normal');
  const [form, setForm] = useState(EMPTY);
  const [pins, setPins] = useState({ cost_center: false, credit: false, program: false, project: false, hazineh: false });
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState([]);
  const [lastPaid, setLastPaid] = useState(0);
  const [delAll, setDelAll] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [pending, setPending] = useState({ visible: false, source: '' });
  const [showPending, setShowPending] = useState(false);
  const [popup, setPopup] = useState(null);
  const [msg, setMsg] = useState(null);
  const [askDel, setAskDel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sub, setSub] = useState(null);
  const statTimer = useRef(null);

  const kind = head ? head.kind : (Number(programKind) === 1 ? 1 : 0);
  const sent = head ? head.sent : !!sendToEvaluation;
  const levels = (head && head.levels) || ['', '', '', '', '', '', ''];
  const year = head ? head.year : '';
  const normal = mode === 'normal';
  const open = fb.loaded && fb.open;
  const say = (m) => { if (m) setMsg(Array.isArray(m) ? m : String(m).split('\n')); };
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const sel = rows.find(r => r.HazinehDocumentDetailId === selId) || null;

  const loadPending = () => API(`/documents/${docId}/pending/`).then(j => { if (j.ok) setPending({ visible: j.visible, source: j.source }); });
  const loadRows = (pick) => {
    setLoading(true);
    return API(`/documents/${docId}/rows/`).then(j => {
      setLoading(false);
      if (!j.ok) { setError(j.msg || ''); return; }
      setError('');
      const data = j.data || [];
      setRows(data);
      const target = data.find(r => r.HazinehDocumentDetailId === (pick ?? selId)) || data[0] || null;
      setSelId(target ? target.HazinehDocumentDetailId : null);
      setForm(target ? fromRow(target) : EMPTY);
      loadPending();
    });
  };

  useEffect(() => {
    if (!open || !docId) return;
    API(`/documents/${docId}/`).then(j => {
      if (!j.ok) { setError(j.msg || ''); return; }
      setHead(j);
      loadRows();
    });
  }, [open, docId]);

  // آمار تنخواه برای محل اعتبار جاری
  const sumNow = num(form.sum_pay) + num(form.added_tax) - num(form.discount);
  const statKey = JSON.stringify([form.buyer_id, form.credit_id, form.program_id, form.project_id, ...HZ.map(k => form[k]), sumNow, mode, lastPaid, selId, head && head.year]);
  useEffect(() => {
    if (!head) return;
    clearTimeout(statTimer.current);
    statTimer.current = setTimeout(() => {
      const last = normal ? sumNow : lastPaid;
      API(`/documents/${docId}/statistics/`, { method: 'POST', body: JSON.stringify({
        BuyerId: form.buyer_id, CreditId: form.credit_id, ProgramId: form.program_id, ProjectId: form.project_id,
        HazinehId: form.hazineh_id, HazinehId1: form.hazineh_id1, HazinehId2: form.hazineh_id2, HazinehId3: form.hazineh_id3,
        HazinehId4: form.hazineh_id4, HazinehId5: form.hazineh_id5, new_paid: sumNow, last_paid: last,
        texts: { buyer: form.buyer, credit: form.credit, program: form.program, project: form.project,
          hz0: form.hz[0], hz1: form.hz[1], hz2: form.hz[2], hz3: form.hz[3], hz4: form.hz[4], hz5: form.hz[5] },
      }) }).then(j => setStats(j.ok ? j.data : []));
    }, 350);
  }, [statKey]);

  const s1 = toEng(search).trim();
  const shown = !s1 ? rows : rows.filter(r => ['Remark', 'Receiver', 'AccountNo', 'BankName', 'FactorNo', 'TaxRefrenceNo', 'ChequeNo']
    .some(k => String(r[k] ?? '').includes(s1)) || Number(r.SumPay) === num(s1));
  const cols = visibleCols(gridCols(levels, head && head.pay_kind), shown);

  const selectRow = (r) => {
    if (!normal) return;
    setSelId(r.HazinehDocumentDetailId);
    setForm(fromRow(r));
  };
  const pickForDelete = (id) => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const editCheck = async () => {
    const j = await API(`/documents/${docId}/edit-check/`);
    if (!j.ok) { say(j.msg); return false; }
    if (j.sent) say(SENT_MSG);
    return !j.blocked;
  };
  const startNew = async () => {
    if (!(await editCheck())) return;
    setLastPaid(0);
    setForm(f => ({
      ...EMPTY,
      factor_no: f.factor_no, factor_date: f.factor_date, buyer_id: f.buyer_id, buyer: f.buyer, shomool: f.shomool,
      cheque_no: f.cheque_no, cheque_date: f.cheque_date,
      is_personel: f.is_personel > 0 ? f.is_personel : 3,
      ...(pins.cost_center ? { cost_center_id: f.cost_center_id, cost_center: f.cost_center } : {}),
      ...(pins.credit ? { credit_id: f.credit_id, credit: f.credit } : {}),
      ...(pins.program ? { program_id: f.program_id, program: f.program } : {}),
      ...(pins.project ? { project_id: f.project_id, project: f.project } : {}),
      ...(pins.hazineh ? { ...HZ.reduce((a, k) => ({ ...a, [k]: f[k] }), {}), hz: f.hz } : {}),
    }));
    setMode('new');
  };
  const startEdit = async () => {
    if (!sel) return;
    if (!(await editCheck())) return;
    setLastPaid(num(form.sum_pay) + num(form.added_tax) - num(form.discount));
    setMode('edit');
  };
  const cancel = () => { setMode('normal'); if (sel) setForm(fromRow(sel)); };
  const save = async () => {
    setSaving(true);
    const body = JSON.stringify({ ...form, quantity: num(form.quantity), unit_price: num(form.unit_price), sum_pay: num(form.sum_pay),
      added_tax: num(form.added_tax), discount: num(form.discount), row: num(form.row) });
    const j = mode === 'new'
      ? await API(`/documents/${docId}/create/`, { method: 'POST', body })
      : await API(`/items/${selId}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (!j.ok) { say(j.msg); return; }
    (j.warnings || []).forEach(w => say(w));
    setMode('normal');
    await loadRows(j.id);
    if (onChanged) onChanged();
  };
  const askDelete = async () => {
    if (!sel) return;
    if (!(await editCheck())) return;
    setAskDel(true);
  };
  const doDelete = async () => {
    setAskDel(false);
    const idx = shown.findIndex(r => r.HazinehDocumentDetailId === selId);
    const j = await API(`/items/${selId}/delete/`, { method: 'DELETE' });
    if (!j.ok) { say(j.msg); return; }
    const next = shown[Math.max(Math.min(idx, shown.length - 2), 0)];
    const after = next && next.HazinehDocumentDetailId !== selId ? next.HazinehDocumentDetailId : null;
    await loadRows(after);
    if (onChanged) onChanged();
  };
  const deleteAll = async () => {
    if (sent) { say(SENT_MSG); return; }
    if (!delAll) { setDelAll(true); setPicked(new Set()); return; }
    if (picked.size === 0) { say('هیچ آرتیکلی انتخاب نشده است'); setDelAll(false); return; }
    // فقط نخستین دستهٔ پیوستهٔ ردیف‌های انتخاب‌شده
    const ids = [];
    let started = false;
    for (const r of shown) {
      if (picked.has(r.HazinehDocumentDetailId)) { started = true; ids.push(r.HazinehDocumentDetailId); } else if (started) break;
    }
    const j = await API(`/documents/${docId}/delete-all/`, { method: 'POST', body: JSON.stringify({ ids }) });
    setDelAll(false); setPicked(new Set());
    if (!j.ok) { say(j.msg); return; }
    await loadRows(null);
    if (onChanged) onChanged();
  };
  const openPending = () => {
    if (sent) { say(SENT_MSG); return; }
    setShowPending(true);
  };
  const makeSerial = async () => {
    const j = await API(`/documents/${docId}/make-serial/`, { method: 'POST' });
    if (!j.ok) { say(j.msg); return; }
    loadRows();
  };
  const showCheques = async () => {
    if (!sel) return;
    const j = await API(`/items/${selId}/cheques/?bt=${encodeURIComponent(sel.BTId || '')}`);
    if (!j.ok) { say(j.msg); return; }
    setPopup({ title: 'چکهای صادر شده', rows: j.data, cols: [
      { key: 'FromAccount', label: 'از حساب', wide: true }, { key: 'ChequeNo', label: 'شماره چک' }, { key: 'IssueDate', label: 'تاریخ صدور' },
      { key: 'BankDate', label: 'تاریخ وصول' }, { key: 'ChequeAmount', label: 'مبلغ چک', money: true },
      { key: 'ChequeRemark', label: 'شرح چک', wide: true }, { key: 'DetailAmount', label: 'مبلغ ریز', money: true },
      { key: 'Receiver', label: 'گیرنده وجه', wide: true }, { key: 'ReceiverAccount', label: 'حساب گیرنده' }, { key: 'Remark', label: 'شرح', wide: true }] });
  };
  const inOutMatch = async () => {
    if (!normal || !sel || num(form.cheque_no) !== 0) return;
    const j = await API(`/items/${selId}/inout-match/`, { method: 'POST' });
    if (!j.ok) { say(j.msg); return; }
    if (!j.data || !j.data.length) return;
    setPopup({ title: 'اسناد هم‌مبلغ', rows: j.data, cols: [
      { key: 'No', label: 'شماره' }, { key: 'DaftarDate', label: 'تاریخ دفتر' }, { key: 'BankDate', label: 'تاریخ بانک' },
      { key: 'Account', label: 'حساب' }, { key: 'Remark', label: 'شرح', wide: true }, { key: 'Amount', label: 'مبلغ', money: true },
      { key: 'DetailRemark', label: 'شرح ریز', wide: true }, { key: 'DetailAmount', label: 'مبلغ ریز', money: true }] });
  };
  const showRejects = async (r) => {
    if (Number(r.RejectCount) <= 0) return;
    const j = await API(`/items/${r.HazinehDocumentDetailId}/rejects/`);
    if (!j.ok) { say(j.msg); return; }
    setPopup({ title: 'شرح واخواهی', rows: j.data, cols: [{ key: 'reason', label: 'شرح واخواهی', wide: true }] });
  };
  const showHistory = async (r) => {
    if (!normal) return;
    const j = await API(`/items/${r.HazinehDocumentDetailId}/history/`);
    if (!j.ok) { say(j.msg); return; }
    if (!j.data.length) return;
    setPopup({ title: 'سوابق تغییرات', rows: j.data, cols: [
      { key: 'pc', label: 'کامپیوتر' }, { key: 'event_date', label: 'تاریخ' }, { key: 'event_time', label: 'ساعت' },
      { key: 'people', label: 'کاربر' }, { key: 'action', label: 'عملیات' }, { key: 'new_value', label: 'مقدار جدید', wide: true }] });
  };
  const informToReceiver = () => {
    if (!sel) return;
    openReport('tadarokat.inform_to_receiver', { id: selId }).catch(e => say(e.message));
  };

  // کد هزینه: انتخاب یک سطح ⇒ تکمیل سطوح بالاتر از سرور
  const pickHazineh = async (level, item) => {
    const ids = HZ.map((k, i) => (i === level ? (item ? item.id : 0) : form[k]));
    const texts = form.hz.map((t, i) => (i === level ? (item ? item.display : '') : t));
    const j = await API('/cascade/', { method: 'POST', body: JSON.stringify({ level, program_kind: kind, year, ids }) });
    if (!j.ok) { say(j.msg); return; }
    setForm(f => {
      const nf = { ...f };
      HZ.forEach((k, i) => { nf[k] = j.ids[i]; });
      nf.hz = j.texts.map((t, i) => t || (j.ids[i] ? texts[i] : ''));
      return nf;
    });
    if (j.messages && j.messages.length) say(j.messages);
  };
  const parentOf = (level) => {
    if (level === 1) return form.hazineh_id;
    for (let i = level - 1; i >= 0; i -= 1) if (form[HZ[i]]) return form[HZ[i]];
    return 0;
  };
  const lk = (name, q, extra = '') => API(`/lookup/${name}/?q=${encodeURIComponent(toEng(q))}&program_kind=${kind}&year=${year}${extra}`);
  const pickTo = (idKey, txtKey, disp = 'display') => ({
    onPick: it => setForm(f => ({ ...f, [idKey]: it.id, [txtKey]: it[disp] })),
    onClear: () => setForm(f => ({ ...f, [idKey]: 0, [txtKey]: '' })),
  });
  const setQty = (k, v) => setForm(f => {
    const nf = { ...f, [k]: toEng(v).replace(/[^0-9]/g, '') };
    if (num(nf.quantity) > 0 && num(nf.unit_price) > 0) nf.sum_pay = String(num(nf.quantity) * num(nf.unit_price));
    return nf;
  });
  const taxKey = (e) => {
    if (e.key === 'F11' && !normal) {
      e.preventDefault();
      const v = num(form.added_tax);
      if (v < 100) F('added_tax', String(Math.round((num(form.sum_pay) - num(form.discount)) * v / 100)));
    }
  };

  const ro = normal;
  const lockGoods = mode === 'edit' && sel && Number(sel.GoodsRequestCount) > 0;
  const amt = (k, lock) => (
    <input style={{ ...ui.input, ...((ro || lock) ? s.ro : {}) }} disabled={ro || lock} value={form[k] === '' ? '' : Number(num(form[k])).toLocaleString('fa-IR')}
      onChange={e => F(k, toEng(e.target.value).replace(/[^0-9]/g, ''))} onKeyDown={k === 'added_tax' ? taxKey : undefined} />
  );
  const txt = (k, lock) => (
    <input style={{ ...ui.input, ...((ro || lock) ? s.ro : {}) }} disabled={ro || lock} value={toFa(form[k])} onChange={e => F(k, toEng(e.target.value))} />
  );
  const canNotes = sel && Number(sel.HavalehNotes) > 0 && String(sel.BTId || '').indexOf(',') === -1 && fb.can('BTNHAVALEHDETAILNOTES');
  const title = 'ریز اقلام سند هزینه';
  const subtitle = head ? `سند شماره ${toFa(head.no)} — ${toFa(head.date)} — سال مالی ${toFa(head.year)}` : '';
  const btnOn = (b) => ({ ...b, opacity: sel ? 1 : 0.4, cursor: sel ? 'pointer' : 'default' });

  return (
    <div style={s.full}>
      <div style={s.header}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>{title}</div>
          <div style={{ fontSize: '12px', color: '#C8A84B' }}>{subtitle}{sent ? ' — ارسال به رسیدگی' : ''}</div>
        </div>
        <button style={ui.closeBtn} onClick={onClose}>✕</button>
      </div>

      {open && (
        <div style={s.toolbar}>
          <input value={search} disabled={!normal} onChange={e => setSearch(e.target.value)} placeholder="جستجو"
            style={{ ...ui.searchInput, width: '200px' }} />
          {normal && !delAll && fb.can('BTNNEW') && <button style={ui.btnNew} onClick={startNew}>جدید</button>}
          {normal && !delAll && fb.can('BTNEDIT') && <button style={btnOn(ui.btnEdit)} disabled={!sel} onClick={startEdit}>ویرایش</button>}
          {normal && !delAll && fb.can('BTNDELETE') && <button style={btnOn(ui.btnDel)} disabled={!sel} onClick={askDelete}>حذف</button>}
          {normal && fb.can('BTNDELETEALL') && (
            <button style={{ ...ui.btnDel, ...(delAll ? { background: '#fff', color: '#c62828', border: '2px solid #c62828' } : {}) }}
              onClick={deleteAll}>{delAll ? 'حذف اقلام انتخابی' : 'حذف گروهی اقلام'}</button>
          )}
          {normal && !delAll && pending.visible && fb.can('BTNHAZINEH') && <button style={ui.btnItems} onClick={openPending}>هزینه های فاقد سند</button>}
          {normal && !delAll && fb.can('BTNNEW') && <button style={btnOn(ui.btnItems)} disabled={!sel} onClick={() => sel && setSub('deduction')}>کسورات</button>}
          {normal && !delAll && fb.can('BTNINFORMTORECEIVER') && <button style={btnOn(ui.btnEdit)} disabled={!sel} onClick={informToReceiver}>اطلاع به ذینفع</button>}
          {normal && !delAll && canNotes && <button style={ui.btnItems} onClick={() => setSub('notes')}>یادداشت</button>}
          {normal && !delAll && fb.can('BTNNEW') && <button style={{ ...ui.btnItems, background: 'linear-gradient(135deg,#4a148c,#7b1fa2)' }} onClick={() => setSub('readpayrol')}>خواندن فیش حقوقی</button>}
          {normal && !delAll && <button style={s.small} onClick={makeSerial}>پشت نمره</button>}
          {normal && <button style={s.link} onClick={showCheques}>چکهای صادر شده</button>}
          <ExcelButton title={title} subtitle={subtitle} columns={cols} rows={shown} />
          {loading && <span>⏳</span>}
        </div>
      )}
      {error && <div style={{ ...ui.errBox, margin: '8px 16px' }}>{error}</div>}

      {open && (
        <div style={s.form}>
          <div style={s.frow}>
            <Field label="ردیف" width="70px">{txt('row')}</Field>
            <Field label="فاکتور: شماره" width="130px">{txt('factor_no', lockGoods)}</Field>
            <Field label="تاریخ " width="150px"><JalaliDatePicker value={form.factor_date} disabled={ro || lockGoods} onChange={v => F('factor_date', v)} /></Field>
            <Field label="فروشنده/گیرنده وجه">
              <Lookup value={form.amount_receiver} disabled={ro || lockGoods} fetcher={q => lk('amount_receiver', q)} {...pickTo('amount_receiver_id', 'amount_receiver', 'title')} />
            </Field>
            <Field label="شرح">
              <Lookup value={form.remark} disabled={ro} fetcher={q => (q ? lk('remark', q) : Promise.resolve({ ok: true, data: [] }))}
                onPick={it => F('remark', it.title)} onClear={() => F('remark', '')} />
            </Field>
            <Field label="تعداد" width="80px">
              <input style={{ ...ui.input, ...((ro || lockGoods) ? s.ro : {}) }} disabled={ro || lockGoods} value={toFa(form.quantity)} onChange={e => setQty('quantity', e.target.value)} />
            </Field>
            <Field label="قیمت واحد" width="130px">
              <input style={{ ...ui.input, ...((ro || lockGoods) ? s.ro : {}) }} disabled={ro || lockGoods}
                value={form.unit_price === '' ? '' : Number(num(form.unit_price)).toLocaleString('fa-IR')} onChange={e => setQty('unit_price', e.target.value)} />
            </Field>
          </div>
          <div style={s.frow}>
            <Field label="مبلغ" width="140px">{amt('sum_pay', lockGoods)}</Field>
            <Field label="تخفیف" width="120px">{amt('discount')}</Field>
            <Field label="مالیات ارزش افزوده" width="130px">{amt('added_tax', lockGoods)}</Field>
            <Field label="جمع" width="140px"><input style={{ ...ui.input, ...s.ro }} disabled value={Number(sumNow).toLocaleString('fa-IR')} /></Field>
            <Field label="نوع هزینه" width="130px">
              <select style={{ ...ui.input, ...(ro ? s.ro : {}) }} disabled={ro} value={form.is_personel} onChange={e => F('is_personel', Number(e.target.value))}>
                {((head && head.is_personel_items) || ['']).map((t, i) => <option key={i} value={i}>{t}</option>)}
              </select>
            </Field>
            {head && Number(head.pay_kind) !== 1 && (
              <Field label="نحوه پرداخت" width="110px">
                <select style={{ ...ui.input, ...(ro ? s.ro : {}) }} disabled={ro} value={form.payment_method} onChange={e => F('payment_method', Number(e.target.value))}>
                  {PAYMENT.map((t, i) => <option key={i} value={i}>{t}</option>)}
                </select>
              </Field>
            )}
            <Field label="کارپرداز">
              <Lookup value={form.buyer} disabled={ro} fetcher={q => lk('buyer', q)} {...pickTo('buyer_id', 'buyer', 'title')} />
            </Field>
            <Field label={<a href={TAX_SITE} target="_blank" rel="noopener noreferrer" style={{ color: '#1a237e' }}>شماره رهگیری سامانه مالیاتی</a>} width="170px">
              {txt('tax_refrence_no')}
            </Field>
            <Field label="تاریخ " width="150px"><JalaliDatePicker value={form.tax_refrence_date} disabled={ro} onChange={v => F('tax_refrence_date', v)} /></Field>
          </div>
          <div style={s.frow}>
            <Field label="مرکز هزینه" extra={<Pin on={pins.cost_center} onToggle={() => setPins(p => ({ ...p, cost_center: !p.cost_center }))} />}>
              <Lookup value={form.cost_center} disabled={ro} fetcher={q => lk('cost_center', q)} {...pickTo('cost_center_id', 'cost_center', 'title')} />
            </Field>
            <Field label="منبع اعتبار" extra={<Pin on={pins.credit} onToggle={() => setPins(p => ({ ...p, credit: !p.credit }))} />}>
              <Lookup value={form.credit} disabled={ro} fetcher={q => lk('credit', q)} {...pickTo('credit_id', 'credit')} />
            </Field>
            <Field label="برنامه/طرح" extra={<Pin on={pins.program} onToggle={() => setPins(p => ({ ...p, program: !p.program }))} />}>
              <Lookup value={form.program} disabled={ro} fetcher={q => lk('program', q)} {...pickTo('program_id', 'program')} />
            </Field>
            <Field label="پروژه" extra={<Pin on={pins.project} onToggle={() => setPins(p => ({ ...p, project: !p.project }))} />}>
              <Lookup value={form.project} disabled={ro} fetcher={q => lk('project', q, `&program_id=${form.program_id}`)} {...pickTo('project_id', 'project')} />
            </Field>
          </div>
          <div style={s.frow}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', paddingBottom: '8px' }}>
              <label style={ui.flabel}>کد هزینه</label>
              <Pin on={pins.hazineh} onToggle={() => setPins(p => ({ ...p, hazineh: !p.hazineh }))} />
            </div>
            {HZ.map((k, i) => (
              <Field key={k} label={levels[i + 1]}>
                <Lookup value={form.hz[i]} disabled={ro || (kind === 1 && i >= 3)}
                  fetcher={q => lk(`hazineh${i}`, q, `&parent=${parentOf(i)}`)}
                  onPick={it => pickHazineh(i, it)} onClear={() => pickHazineh(i, null)} />
              </Field>
            ))}
          </div>
          <div style={s.frow}>
            <Field label="شمول" width="120px">
              <select style={{ ...ui.input, ...(ro ? s.ro : {}) }} disabled={ro} value={form.shomool} onChange={e => F('shomool', Number(e.target.value))}>
                {SHOMOOL.map((t, i) => <option key={i} value={i}>{t}</option>)}
              </select>
            </Field>
            <Field label="شماره ستاد" width="150px">{txt('setad_no')}</Field>
            <Field label="شناسه قبض" width="160px">{txt('bill_id')}</Field>
            <Field label="شناسه پرداخت" width="160px">{txt('slip_id')}</Field>
            <Field label="چک: شماره" width="120px">
              <input style={{ ...ui.input, ...s.ro }} readOnly value={toFa(form.cheque_no)} onDoubleClick={inOutMatch} />
            </Field>
            <Field label="تاریخ" width="110px"><input style={{ ...ui.input, ...s.ro }} readOnly value={toFa(form.cheque_date)} /></Field>
            {!normal && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginRight: 'auto' }}>
                {fb.can('BTNCANCEL') && <button style={ui.btnCancel} onClick={cancel}>لغو</button>}
                {fb.can('BTNOK') && fb.can(mode === 'new' ? 'BTNNEW' : 'BTNEDIT') && (
                  <button style={ui.btnSave} disabled={saving} onClick={save}>{saving ? '⏳' : 'ثبت'}</button>)}
              </div>
            )}
          </div>
        </div>
      )}

      {open && (
        <div style={{ ...ui.splitWrap, flex: 1, minHeight: 0, background: '#fff' }}>
          <div style={{ ...ui.topPane, opacity: normal ? 1 : 0.6, pointerEvents: normal ? 'auto' : 'none' }}>
            <Grid cols={cols} rows={shown} idKey="HazinehDocumentDetailId" selId={selId} onSelect={selectRow}
              onRowHeader={showHistory} onContext={showRejects}
              multi={delAll} picked={picked} onPick={pickForDelete} />
          </div>
          {stats.length > 0 && (
            <div style={{ ...ui.bottomPane, flex: '0 1 30%', minHeight: '120px' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
                <ExcelButton title="موجودی تنخواه" columns={STAT_COLS} rows={stats} />
              </div>
              <Grid cols={STAT_COLS} rows={stats} />
            </div>
          )}
        </div>
      )}

      {showPending && (
        <PendingPanel docId={docId} initSource={pending.source} canAdd={fb.can('BTNNEW')} say={say}
          form={{ credit_id: form.credit_id, program_id: form.program_id, project_id: form.project_id, cost_center_id: form.cost_center_id,
            hazineh_id: form.hazineh_id, hazineh_id1: form.hazineh_id1, hazineh_id2: form.hazineh_id2, hazineh_id3: form.hazineh_id3,
            hazineh_id4: form.hazineh_id4, hazineh_id5: form.hazineh_id5, buyer_id: form.buyer_id,
            payment_method: form.payment_method, is_personel: form.is_personel }}
          onClose={() => setShowPending(false)}
          onAdded={async (id) => { setShowPending(false); await loadRows(id || null); if (onChanged) onChanged(); }} />
      )}
      {sub === 'deduction' && sel && (
        <HazinehDocumentDetailDeduction user={user} detailId={selId} detailAmount={num(sel.SumPay)} sendToEvaluation={sent}
          onClose={() => { setSub(null); loadRows(selId); if (onChanged) onChanged(); }} />
      )}
      {sub === 'notes' && sel && (
        <HavalehDetailNotes user={user} budgetTransactionId={sel.BTId} hazinehDocumentId={0} contractorRequestId={0} onClose={() => setSub(null)} />
      )}
      {sub === 'readpayrol' && (
        <ReadPayrolHazineh documentId={documentId}
          onClose={() => setSub(null)}
          onDone={async () => { await loadRows(null); if (onChanged) onChanged(); }} />
      )}
      {popup && <Popup {...popup} onClose={() => setPopup(null)} />}
      {askDel && (
        <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر" tone="danger"
          onConfirm={doDelete} onCancel={() => setAskDel(false)} />
      )}
      {msg && <WarningDialog title="پیام" lines={msg} confirmLabel="تایید" cancelLabel="بستن" onConfirm={() => setMsg(null)} onCancel={() => setMsg(null)} />}
    </div>
  );
}

const s = {
  full: { position: 'fixed', inset: 0, zIndex: 950, background: '#f0f2f7', display: 'flex', flexDirection: 'column', direction: 'rtl', fontFamily: "'Vazirmatn',Tahoma,sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: 'linear-gradient(135deg,#0a1628,#1a2f50)', color: '#fff' },
  toolbar: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e6e8ef' },
  form: { padding: '10px 16px 4px', background: '#fff', borderBottom: '2px solid #e0e0e0', overflowY: 'auto', maxHeight: '45vh' },
  frow: { display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'flex-end' },
  ro: { background: '#f5f5f5', color: '#333' },
  wide: { whiteSpace: 'normal', minWidth: '160px', textAlign: 'right' },
  drop: { position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto', zIndex: 1100, boxShadow: '0 6px 18px rgba(0,0,0,0.15)' },
  dropItem: { padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3' },
  xBtn: { padding: '0 10px', background: '#e0e0e0', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  pin: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: 0 },
  small: { padding: '8px 12px', background: '#fff', border: '1.5px solid #1a2f50', color: '#1a2f50', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700 },
  link: { background: 'none', border: 'none', color: '#1a237e', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px' },
  mini: { width: '32px', height: '32px', background: '#1a2f50', color: '#C8A84B', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 700 },
  radio: { fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' },
};
